'use client';

import React, { useEffect, useState } from 'react';
import Button from '@/components/atoms/button';
import DatePicker from '@/components/atoms/datepicker';
import Input from '@/components/atoms/Input';
import { isComparePassword, isValidEmail, notify } from '@/utils/funtion';
import { signUp } from '@/services/apis/modules/auth';
import { errorsByMessages, errorsMessage } from '@/libs/errors';
import Checkbox from '@/components/atoms/checkbox';
import Link from 'next/link';

interface SignUpProps {
  handleChangeForm?: (value: number) => void;
}

const SignUp: React.FC<SignUpProps> = ({ handleChangeForm }) => {
  const [signup, setSignUp] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthday: undefined as any,
    password: '',
    passwordConfirm: '',
    isAgreePolicy: false,
    loading: false,
  });
  const [signUpError, setSignUpError] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    passwordConfirm: '',
    isAgreePolicy: '',
  });

  const [validatePassword, setValidatePassword] = useState({
    min: false,
    special: false,
    cap: false,
  });

  const handleResetForm = () => {
    setSignUp({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      birthday: undefined as any,
      password: '',
      passwordConfirm: '',
      isAgreePolicy: false,
      loading: false,
    });
    setSignUpError({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      passwordConfirm: '',
      isAgreePolicy: '',
    });
    setValidatePassword({ min: false, special: false, cap: false });
  };

  useEffect(() => {
    if (signup.password) {
      setValidatePassword({
        min: signup.password.length >= 8,
        special: /[!@#$%^&*(),.?":{}|<>]/.test(signup.password),
        cap: /[A-Z]/.test(signup.password),
      });
    }
  }, [signup.password]);

  const handleValidateSignUp = () => {
    if (
      !signup.firstName?.trim() ||
      !signup.lastName?.trim() ||
      !signup.email?.trim() ||
      !isValidEmail(signup.email) ||
      !signup.phoneNumber?.trim() ||
      signup.phoneNumber.length <= 9 ||
      !signup.password?.trim() ||
      !validatePassword.cap ||
      !validatePassword.min ||
      !validatePassword.special ||
      !signup.passwordConfirm?.trim() ||
      !isComparePassword(signup.password, signup.passwordConfirm) ||
      !signup.isAgreePolicy
    ) {
      setSignUpError({
        ...signUpError,
        firstName: !signup.firstName?.trim() ? 'Họ là trường bắt buộc' : '',
        lastName: !signup.lastName?.trim() ? 'Họ là trường bắt buộc' : '',
        email: !signup.email?.trim()
          ? 'Email là trường bắt buộc'
          : !isValidEmail(signup.email)
            ? 'Email không hợp lệ'
            : '',
        phoneNumber: !signup.phoneNumber?.trim()
          ? 'Số điện thoại là trường bắt buộc'
          : signup.phoneNumber.length <= 9
            ? 'Số điện thoại không hợp lệ'
            : '',
        password: !signup.password?.trim()
          ? 'Mật khẩu là trường bắt buộc'
          : !validatePassword.cap ||
              !validatePassword.min ||
              !validatePassword.special
            ? 'Mật khẩu không hợp lệ'
            : '',
        passwordConfirm: !signup.passwordConfirm?.trim()
          ? 'Xác nhận mật khẩu là trường bắt buộc'
          : !isComparePassword(signup.password, signup.passwordConfirm)
            ? 'Xác nhận mật khẩu không chính xác'
            : '',
        isAgreePolicy: !signup.isAgreePolicy
          ? 'Bạn cần đồng ý với Chính sách và Điều khoản'
          : '',
      });
      notify(
        'error',
        'Vui lòng nhập các thông tin để đăng ký tài khoản',
        'top-center',
        5000,
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!handleValidateSignUp()) return;
    const body = {
      email: signup.email,
      first_name: signup.firstName,
      last_name: signup.lastName,
      phone_number: signup.phoneNumber,
      birthday: signup.birthday,
      password: signup.password,
    };

    setSignUp({
      ...signup,
      loading: true,
    });

    try {
      const res = await signUp(body);
      if (res?.status) {
        notify('success', 'Đăng ký thành công', 'top-center', 4000);
        handleResetForm();
        if (handleChangeForm) handleChangeForm(0);
      } else {
        const errors =
          errorsMessage[res?.key] || 'Đã xảy ra lỗi, vui lòng thử lại.';
        const errorMessage =
          errorsByMessages[res?.message] || 'Đã xảy ra lỗi, vui lòng thử lại.';
        if (res?.key) {
          notify('error', errors, 'top-center', 5000);
        } else {
          notify('error', errorMessage, 'top-center', 5000);
        }
      }
      setSignUp({
        ...signup,
        loading: false,
      });
    } catch (error: any) {
      setSignUp({
        ...signup,
        loading: false,
      });
    }
  };

  return (
    <div className="t-signup">
      <h1>Đăng ký tài khoản</h1>
      <form>
        <div className="t-signup_row">
          <Input
            isRequired
            label="Họ"
            autoFocus
            value={signup.firstName}
            onChange={(event) => {
              setSignUp({
                ...signup,
                firstName: event.target.value,
              });
              setSignUpError({
                ...signUpError,
                firstName: '',
              });
            }}
            errorMessage={signUpError.firstName}
          />
          <Input
            isRequired
            label="Tên"
            value={signup.lastName}
            errorMessage={signUpError.lastName}
            onChange={(event) => {
              setSignUp({
                ...signup,
                lastName: event.target.value,
              });
              setSignUpError({
                ...signUpError,
                lastName: '',
              });
            }}
          />
        </div>
        <div className="t-signup_row">
          <Input
            isRequired
            label="Email"
            value={signup.email}
            errorMessage={signUpError.email}
            onChange={(event) => {
              setSignUp({
                ...signup,
                email: event.target.value.toLowerCase(),
              });
              setSignUpError({
                ...signUpError,
                email: '',
              });
            }}
          />
          <Input
            isRequired
            label="Số điện thoại"
            value={signup.phoneNumber}
            errorMessage={signUpError.phoneNumber}
            onChange={(event) => {
              setSignUp({
                ...signup,
                phoneNumber: event.target.value,
              });
              setSignUpError({
                ...signUpError,
                phoneNumber: '',
              });
            }}
          />
        </div>
        <div className="t-signup_row">
          <ul>
            <li>
              <span
                style={{
                  border: `1px solid ${validatePassword.min ? 'green' : 'red'}`,
                  backgroundColor: validatePassword.min ? 'green' : 'white',
                }}
              >
                {validatePassword.min ? '✓' : ''}
              </span>
              <p>Ít nhất 8 kí tự</p>
            </li>
            <li>
              <span
                style={{
                  border: `1px solid ${validatePassword.special ? 'green' : 'red'}`,
                  backgroundColor: validatePassword.special ? 'green' : 'white',
                }}
              >
                {validatePassword.special ? '✓' : ''}
              </span>
              <p>Có chứa kí tự đặc biệt</p>
            </li>
            <li>
              <span
                style={{
                  border: `1px solid ${validatePassword.cap ? 'green' : 'red'}`,
                  backgroundColor: validatePassword.cap ? 'green' : 'white',
                }}
              >
                {validatePassword.cap ? '✓' : ''}
              </span>
              <p>Ít nhất 1 chữ cái viết hoa</p>
            </li>
          </ul>
          <DatePicker
            label="Ngày sinh"
            placeholder="DD-MM-YYYY"
            value={signup.birthday}
            onChange={(date) => {
              setSignUp({
                ...signup,
                birthday: date,
              });
            }}
          />
        </div>
        <div className="t-signup_row">
          <Input
            isRequired
            label="Mật khẩu"
            type="password"
            value={signup.password}
            errorMessage={signUpError.password}
            onChange={(event) => {
              setSignUp({
                ...signup,
                password: event.target.value,
              });
              setSignUpError({
                ...signUpError,
                password: '',
              });
            }}
          />
          <Input
            isRequired
            type="password"
            label="Xác nhận lại mật khẩu"
            value={signup.passwordConfirm}
            errorMessage={signUpError.passwordConfirm}
            onChange={(event) => {
              setSignUp({
                ...signup,
                passwordConfirm: event.target.value,
              });
              setSignUpError({
                ...signUpError,
                passwordConfirm: '',
              });
            }}
          />
        </div>
        <div className="t-signup_policy">
          <Checkbox
            checked={signup.isAgreePolicy}
            handleOnCheck={(event) => {
              setSignUp({
                ...signup,
                isAgreePolicy: event.target.checked,
              });
              setSignUpError({
                ...signUpError,
                isAgreePolicy: '',
              });
            }}
            errorMessage={signUpError.isAgreePolicy}
            label={
              <>
                Tôi đồng ý với{' '}
                <Link
                  href="/chinh-sach?policy_type=chinh-sach-bao-ve"
                  target="_blank"
                >
                  Chính sách bảo vệ dữ liệu cá nhân
                </Link>
                ,{' '}
                <Link
                  href="/chinh-sach?policy_type=dieu-khoan-dieu-kien-chung"
                  target="_blank"
                >
                  Điều khoản và điều kiện chung
                </Link>{' '}
                của Vietstats.
              </>
            }
          />
        </div>
        <div className="t-signup_button">
          <Button
            type="reset"
            ariaLabel="Đăng ký tài khoản ngay"
            shape="fill"
            variant="danger"
            onClick={handleSubmit}
            loading={signup.loading}
          >
            Đăng ký ngay
          </Button>
        </div>
        <div className="t-signup_signin ">
          <span>Bạn đã có tài khoản?</span>
          <a
            onClick={() => {
              if (handleChangeForm) handleChangeForm(0);
              handleResetForm();
            }}
          >
            Đăng nhập ngay!
          </a>
        </div>
      </form>
    </div>
  );
};

export default SignUp;
